import React, { useState, useMemo } from 'react';
import Seo from './common/Seo';
import { FaCalculator, FaWhatsapp, FaInfoCircle, FaCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../constants';

// Precios base por hora (en pesos mexicanos)
const basePrices = {
  'enfermeria-general': 180,
  'enfermeria-especializada': 250,
  'cuidadora': 120,
  'auxiliar-enfermeria': 150,
  'enfermeria-24h': 200,
  'cuidados-geriatricos': 170,
  'postoperatorio': 220
};

const CostCalculator = () => {
  const [formData, setFormData] = useState({
    serviceType: '',
    hoursPerDay: 8,
    daysPerWeek: 5,
    includesWeekends: false,
    isNightShift: false,
    needsSpecialCare: false,
    contractDuration: 1
  });

  const serviceOptions = [
    { value: 'enfermeria-general', label: 'Enfermería General' },
    { value: 'enfermeria-especializada', label: 'Enfermería Especializada' },
    { value: 'cuidadora', label: 'Cuidadora' },
    { value: 'auxiliar-enfermeria', label: 'Auxiliar de Enfermería' },
    { value: 'enfermeria-24h', label: 'Enfermería 24 Horas' },
    { value: 'cuidados-geriatricos', label: 'Cuidados Geriátricos' },
    { value: 'postoperatorio', label: 'Cuidados Postoperatorios' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculation = useMemo(() => {
    if (!formData.serviceType) return null;

    const basePrice = basePrices[formData.serviceType] || 150;
    let pricePerHour = basePrice;

    // Ajustes de precio
    if (formData.isNightShift) pricePerHour *= 1.25; // +25% turno nocturno
    if (formData.needsSpecialCare) pricePerHour *= 1.15; // +15% cuidados especiales
    if (formData.includesWeekends) pricePerHour *= 1.1; // +10% fines de semana

    // Descuentos por contrato largo
    let discount = 0;
    if (formData.contractDuration >= 6) discount = 0.15;
    else if (formData.contractDuration >= 3) discount = 0.1;
    else if (formData.contractDuration >= 1) discount = 0.05;

    const hoursPerWeek = formData.hoursPerDay * formData.daysPerWeek;
    const weeksPerMonth = 4.33;
    const hoursPerMonth = hoursPerWeek * weeksPerMonth;
    
    const monthlySubtotal = hoursPerMonth * pricePerHour;
    const discountAmount = monthlySubtotal * discount;
    const monthlyTotal = monthlySubtotal - discountAmount;
    const dailyCost = (monthlyTotal / weeksPerMonth) / formData.daysPerWeek;
    const hourlyEffective = monthlyTotal / hoursPerMonth;

    return {
      pricePerHour: pricePerHour.toFixed(2),
      hoursPerMonth: Math.round(hoursPerMonth),
      monthlySubtotal: monthlySubtotal.toFixed(2),
      discount: (discount * 100).toFixed(0),
      discountAmount: discountAmount.toFixed(2),
      monthlyTotal: monthlyTotal.toFixed(2),
      dailyCost: dailyCost.toFixed(2),
      hourlyEffective: hourlyEffective.toFixed(2)
    };
  }, [formData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const benefits = [
    "Personal capacitado y certificado",
    "Supervisión constante",
    "Reemplazo inmediato en caso necesario",
    "Sin costo de contratación",
    "Atención 24/7 para emergencias",
    "Flexibilidad de horarios"
  ];

  return (
    <>
      <Seo
        title={`Calculadora de Costos | ${COMPANY_INFO.name}`}
        description="Calcula el costo estimado de nuestros servicios de enfermería a domicilio. Precios transparentes y competitivos en Guadalajara."
        canonical="https://geriatriaserena.com/calculadora-costos"
      />

      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <FaCalculator className="text-6xl mx-auto mb-6 text-blue-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Calculadora de Costos
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Obtén un estimado del costo de nuestros servicios de enfermería a domicilio
            </p>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-16 -mt-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Configura tu Servicio
                </h2>

                <div className="space-y-6">
                  {/* Service Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Servicio *
                    </label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecciona un servicio</option>
                      {serviceOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Hours per day */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Horas por día: {formData.hoursPerDay}
                    </label>
                    <input
                      type="range"
                      name="hoursPerDay"
                      min="4"
                      max="24"
                      value={formData.hoursPerDay}
                      onChange={handleChange}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>4 horas</span>
                      <span>24 horas</span>
                    </div>
                  </div>

                  {/* Days per week */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Días por semana: {formData.daysPerWeek}
                    </label>
                    <input
                      type="range"
                      name="daysPerWeek"
                      min="1"
                      max="7"
                      value={formData.daysPerWeek}
                      onChange={handleChange}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>1 día</span>
                      <span>7 días</span>
                    </div>
                  </div>

                  {/* Contract Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duración del contrato (meses): {formData.contractDuration}
                    </label>
                    <input
                      type="range"
                      name="contractDuration"
                      min="1"
                      max="12"
                      value={formData.contractDuration}
                      onChange={handleChange}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>1 mes</span>
                      <span>12 meses</span>
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isNightShift"
                        checked={formData.isNightShift}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Turno nocturno (+25%)</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="includesWeekends"
                        checked={formData.includesWeekends}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Incluye fines de semana (+10%)</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="needsSpecialCare"
                        checked={formData.needsSpecialCare}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Requiere cuidados especiales (+15%)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Estimación de Costos
                  </h2>

                  {calculation ? (
                    <div className="space-y-4">
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600">Precio por hora base:</span>
                        <span className="font-semibold">{formatCurrency(calculation.pricePerHour)}</span>
                      </div>
                      
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600">Horas mensuales:</span>
                        <span className="font-semibold">{calculation.hoursPerMonth} hrs</span>
                      </div>
                      
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600">Subtotal mensual:</span>
                        <span className="font-semibold">{formatCurrency(calculation.monthlySubtotal)}</span>
                      </div>
                      
                      {parseFloat(calculation.discount) > 0 && (
                        <div className="flex justify-between py-3 border-b text-green-600">
                          <span>Descuento ({calculation.discount}%):</span>
                          <span className="font-semibold">-{formatCurrency(calculation.discountAmount)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between py-4 bg-blue-50 rounded-lg px-4 mt-4">
                        <span className="text-xl font-bold text-gray-900">Total Mensual:</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {formatCurrency(calculation.monthlyTotal)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <div className="text-sm text-gray-500">Costo diario</div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatCurrency(calculation.dailyCost)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <div className="text-sm text-gray-500">Costo por hora</div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatCurrency(calculation.hourlyEffective)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2 mt-6 p-4 bg-amber-50 rounded-lg">
                        <FaInfoCircle className="text-amber-600 flex-shrink-0 mt-1" />
                        <p className="text-sm text-amber-800">
                          Este es un estimado. El precio final puede variar según las necesidades específicas del paciente. Contáctanos para una cotización personalizada.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <FaCalculator className="text-5xl mx-auto mb-4 text-gray-300" />
                      <p>Selecciona un tipo de servicio para ver el estimado</p>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-3">¿Te interesa este servicio?</h3>
                  <p className="text-green-100 mb-4">
                    Contáctanos para recibir una cotización personalizada sin compromiso
                  </p>
                  <a
                    href={`https://wa.me/${COMPANY_INFO.whatsapp}?text=Hola, me interesa cotizar el servicio de ${formData.serviceType ? serviceOptions.find(s => s.value === formData.serviceType)?.label : 'enfermería'}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-green-600 px-6 py-3 rounded-lg font-bold inline-flex items-center gap-2 hover:bg-green-50 transition-colors"
                  >
                    <FaWhatsapp className="text-xl" />
                    Cotizar por WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Lo que Incluyen Nuestros Servicios
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                  <FaCheck className="text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/services"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Ver todos nuestros servicios →
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default CostCalculator;
